import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * A list page's filter/action bar that survives a 375px viewport (audit H-03).
 *
 * The failure this replaces: a `flex items-center gap-2` row with a hard
 * `w-72` search input plus four action controls has a min-content width of
 * ~650px, so the whole page scrolls horizontally on a phone. Two rules fix it
 * and both are easy to forget by hand —
 *   1. wrap (`flex-wrap`), so overflowing controls move to a second line
 *   2. let the search field be full-width below `sm` instead of fixed
 *
 * DashboardPage documents the related trap: a nowrap flex child keeps
 * `min-width: auto` and forces the page wide, which is why `min-w-0` appears
 * on the slots here.
 */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>
}

/** The search/filter field. Full-width on phones, fixed once there's room. */
function ToolbarSearch({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('w-full min-w-0 sm:w-72', className)}>{children}</div>
}

/** Buttons and menus; pushed right once the row fits on one line. */
function ToolbarActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-2 sm:ml-auto', className)}>
      {children}
    </div>
  )
}

Toolbar.Search = ToolbarSearch
Toolbar.Actions = ToolbarActions
